import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getCategories,
  getSubcategories,
  updateCategory,
  updateSubcategory,
  removeCategory,
  removeSubcategory
} from 'slice/categorySlice';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';

const Update = () => {
  const dispatch = useDispatch();
  const { categories, subcategories, loading } = useSelector((state) => state.category);

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');

  const [showSubModal, setShowSubModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [subForm, setSubForm] = useState({ category_id: '', name: '' });

  useEffect(() => {
    dispatch(getCategories());
    dispatch(getSubcategories());
  }, [dispatch]);

  const categoryMap = useMemo(() => {
    const map = {};
    (categories || []).forEach((cat) => {
      map[cat.id] = cat.name;
    });
    return map;
  }, [categories]);

  const handleEditCategory = (item) => {
    setSelectedCategory(item);
    setCategoryName(item.name);
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    await dispatch(updateCategory({ id: selectedCategory.id, data: { name: categoryName.trim() } }));
    toast.success('Category updated successfully');
    setShowCategoryModal(false);
    setSelectedCategory(null);
  };

  const handleEditSub = (item) => {
    setSelectedSub(item);
    setSubForm({ category_id: item.category_id, name: item.name });
    setShowSubModal(true);
  };

  const handleSaveSub = async () => {
    if (!subForm.category_id) {
      toast.error('Please select a category');
      return;
    }
    await dispatch(
      updateSubcategory({
        id: selectedSub.id,
        data: { category_id: subForm.category_id, name: subForm.name.trim() }
      })
    );
    toast.success('Sub-category updated successfully');
    setShowSubModal(false);
    setSelectedSub(null);
  };

  const closeModal = () => {
    setShowCategoryModal(false);
    setShowSubModal(false);
    setSelectedCategory(null);
    setSelectedSub(null);
  };

  const handleRemoveCategory = (id) => {
    if (!window.confirm('Delete this category? Its sub-categories will also be deleted.')) return;
    dispatch(removeCategory(id));
    toast.success('Category removed successfully');
  };

  const handleRemoveSub = (id) => {
    if (!window.confirm('Delete this sub-category?')) return;
    dispatch(removeSubcategory(id));
    toast.success('Sub-category removed successfully');
  };

  const categoryHeaders = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Category Name', accessor: 'name' },
    {
      Header: 'Sub-Categories',
      id: 'subCount',
      Cell: ({ row }) =>
        (subcategories || []).filter((sub) => String(sub.category_id) === String(row.original.id)).length
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn btn-danger mx-1" onClick={() => handleRemoveCategory(row.original.id)}>
            Remove
          </button>
          <button className="btn btn-primary mx-1" onClick={() => handleEditCategory(row.original)}>
            Edit
          </button>
        </>
      )
    }
  ];

  const subcategoryHeaders = [
    { Header: 'ID', accessor: 'id' },
    {
      Header: 'Parent Category',
      accessor: 'category_id',
      Cell: ({ value }) => categoryMap[value] || `#${value}`
    },
    { Header: 'Sub-Category Name', accessor: 'name' },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn btn-danger mx-1" onClick={() => handleRemoveSub(row.original.id)}>
            Remove
          </button>
          <button className="btn btn-primary mx-1" onClick={() => handleEditSub(row.original)}>
            Edit
          </button>
        </>
      )
    }
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Update Category</h4>
        </div>

        <AdminTable tableHeaders={categoryHeaders} tableData={categories || []} />
        {loading && <p className="text-white mx-4 mt-2">Updating...</p>}
        {!loading && categories?.length === 0 && (
          <p className="text-white mx-4">No categories found.</p>
        )}

        <div className="mainheadig mx-4 mt-5">
          <h4 className="text-white font-weight-bold">Update Sub-Category</h4>
        </div>

        <AdminTable tableHeaders={subcategoryHeaders} tableData={subcategories || []} />
        {!loading && subcategories?.length === 0 && (
          <p className="text-white mx-4">No sub-categories found.</p>
        )}
      </div>

      {/* ================= EDIT CATEGORY MODAL ================= */}
      {showCategoryModal && selectedCategory && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Edit Category</h5>
                <button type="button" className="close" onClick={closeModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <label htmlFor="editCategoryName" className="adminlables">
                  Category Name
                </label>
                <input
                  type="text"
                  id="editCategoryName"
                  className="form-control"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSaveCategory}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= EDIT SUB-CATEGORY MODAL ================= */}
      {showSubModal && selectedSub && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Edit Sub-Category</h5>
                <button type="button" className="close" onClick={closeModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <label className="adminlables" htmlFor="subCatParent">Parent Category</label>
                <select
                  id="subCatParent"
                  className="form-control mb-2"
                  value={subForm.category_id}
                  onChange={(e) => setSubForm({ ...subForm, category_id: e.target.value })}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>

                <label className="adminlables" htmlFor="subCatName">Sub-Category Name</label>
                <input
                  id="subCatName"
                  type="text"
                  className="form-control"
                  value={subForm.name}
                  onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSaveSub}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Update;